import FmdGoodIcon from '@mui/icons-material/FmdGood';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import NotesOutlinedIcon from '@mui/icons-material/NotesOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import { Icon, MenuItem, TextField, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import {
  Control,
  Controller,
  FieldErrors,
  FormState,
  UseFormRegister,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch,
} from 'react-hook-form';

import AddressAutocomplete from '@/components/address-autocomplete';
import Button from '@/components/Button';
import InputField from '@/components/InputField';
import useResponsive from '@/hooks/useResponsive';

import type { AddressDetails } from '@/components/address-autocomplete';

import { SendToWarehouseFormData } from '../../inventorySchema';
import {
  getPackageSizeOptions,
  PICKUP_COUNTRIES,
  pickupCountryRequiresZipcode,
} from './pickupConstants';

interface PickupDetailsProps {
  step: number;
  setCurrentStep: (step: number) => void;
  errors: FieldErrors<SendToWarehouseFormData>;
  touchedFields: FormState<SendToWarehouseFormData>['touchedFields'];
  control: Control<SendToWarehouseFormData>;
  register: UseFormRegister<SendToWarehouseFormData>;
  setValue: UseFormSetValue<SendToWarehouseFormData>;
  watch: UseFormWatch<SendToWarehouseFormData>;
  trigger: UseFormTrigger<SendToWarehouseFormData>;
  editStep: boolean;
  isDisabled: boolean;
  isAddressConfirmed: boolean;
  setIsAddressConfirmed: (value: boolean) => void;
  isAddressTouched: boolean;
  setIsAddressTouched: (value: boolean) => void;
  userCountry: string;
  userCountryCode: string;
}

const PickupDetails = ({
  step,
  setCurrentStep,
  errors,
  touchedFields,
  control,
  register,
  setValue,
  watch,
  trigger,
  editStep,
  isDisabled,
  isAddressConfirmed,
  setIsAddressConfirmed,
  isAddressTouched,
  setIsAddressTouched,
  userCountry,
  userCountryCode,
}: PickupDetailsProps) => {
  const theme = useTheme();
  const isSmallDrawer = useResponsive('between', 'lg', 'xl');
  const isPhone = useResponsive('down', 'sm');

  const [hasUnsupportedCountryError, setHasUnsupportedCountryError] = useState(false);

  const selectedCountry = watch('pickup.country') || '';
  const requiresZipcode = pickupCountryRequiresZipcode(selectedCountry);
  const packageSizeOptions = getPackageSizeOptions();

  useEffect(() => {
    if (selectedCountry) {
      trigger('pickup.postalCode');
    }
  }, [selectedCountry, trigger]);

  const findAddressComponent = (
    components: google.maps.GeocoderAddressComponent[] | undefined,
    type: string,
  ): string => {
    if (!components) return '';
    const component = components.find((c) => c.types.includes(type));
    return component?.short_name || '';
  };

  const onAddressChange = (details: AddressDetails) => {
    if (details) {
      setValue('pickup.address', details.address);

      const city =
        details.city ||
        findAddressComponent(details.gmaps?.address_components, 'locality') ||
        findAddressComponent(details.gmaps?.address_components, 'administrative_area_level_2');

      const postalCode =
        details.zipCode || findAddressComponent(details.gmaps?.address_components, 'postal_code');
      const countryCode =
        details.country || findAddressComponent(details.gmaps?.address_components, 'country');

      const isSupportedCountry = PICKUP_COUNTRIES.some((c) => c.code === countryCode);

      if (!isSupportedCountry) {
        setHasUnsupportedCountryError(true);
        setIsAddressConfirmed(false);
        return;
      }

      setValue('pickup.city', city);
      setValue('pickup.postalCode', postalCode);

      setIsAddressConfirmed(true);
      setHasUnsupportedCountryError(false);
      trigger('pickup');
    }
  };

  const onAddressManualChange = () => {
    setIsAddressConfirmed(false);
    setHasUnsupportedCountryError(false);
  };

  const onAddressBlur = () => {
    setIsAddressTouched(true);
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          margin: '1rem 0',
          [theme.breakpoints.between('lg', 'xl')]: {
            flexDirection: 'column',
            gap: '1rem',
          },
          [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            gap: '1rem',
          },
        }}
      >
        <Typography variant="subtitle1" sx={{ minWidth: '4.1rem' }}>
          Paso <span style={{ color: theme.palette.primary.main }}>4</span>
        </Typography>
        <Divider
          orientation={isSmallDrawer || isPhone ? 'horizontal' : 'vertical'}
          flexItem
          sx={{
            margin: isSmallDrawer || isPhone ? '0' : `0 ${theme.spacing(2)}`,
          }}
        />
        <Box>
          <Typography variant="subtitle2">Datos de recolección a domicilio</Typography>
          <Typography
            variant="body1"
            fontWeight="bold"
            sx={{
              marginTop: '0.25rem',
              '&& span': {
                color: theme.palette.primary.main,
              },
            }}
          >
            <span>Completa los datos</span> para programar la recolección de tus productos
          </Typography>
        </Box>
      </Box>

      <Paper
        elevation={3}
        sx={{
          padding: theme.spacing(3),
        }}
      >
        <Typography variant="subtitle2" mb={2}>
          Información de recolección
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="body2" mb={1} fontWeight="bold">
              Tamaño de paquete *
            </Typography>
            <Controller
              name="pickup.packageSize"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  label="Selecciona el tamaño"
                  error={touchedFields?.pickup?.packageSize && !!errors?.pickup?.packageSize}
                  helperText={
                    touchedFields?.pickup?.packageSize ? errors?.pickup?.packageSize?.message : ''
                  }
                  slotProps={{
                    input: {
                      startAdornment: (
                        <Icon color="disabled" sx={{ mr: 1 }}>
                          <LocalShippingOutlinedIcon />
                        </Icon>
                      ),
                    },
                  }}
                >
                  {packageSizeOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={3}
        sx={{
          padding: theme.spacing(3),
          mt: 2,
        }}
      >
        <Typography variant="subtitle2" mb={2}>
          Dirección de recolección
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Icon color="disabled" sx={{ marginRight: '0.5rem' }}>
                <FmdGoodOutlinedIcon />
              </Icon>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.secondary.main,
                  fontSize: '1rem',
                }}
              >
                Dirección *
              </Typography>
            </Box>
            <Controller
              name="pickup.address"
              control={control}
              render={({ field }) => (
                <AddressAutocomplete
                  size="medium"
                  sx={{ marginTop: '0.5rem' }}
                  error={Boolean(isAddressTouched && !isAddressConfirmed)}
                  value={field.value}
                  placeholder="Ingresa la dirección de recolección"
                  country={
                    PICKUP_COUNTRIES.find((c) => c.value === userCountryCode)?.code.toLowerCase() ||
                    'mx'
                  }
                  onSelectAddress={(details) => {
                    field.onChange(details.address);
                    onAddressChange(details);
                  }}
                  onChange={(value: string) => {
                    field.onChange(value);
                    onAddressManualChange();
                  }}
                  onBlur={onAddressBlur}
                />
              )}
            />
            {isAddressTouched && !isAddressConfirmed && !hasUnsupportedCountryError && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.error.main,
                  display: 'block',
                  mt: 0.5,
                }}
              >
                Por favor selecciona una dirección válida de las sugerencias
              </Typography>
            )}
            {hasUnsupportedCountryError && (
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.error.main,
                  display: 'block',
                  mt: 0.5,
                  fontWeight: 'bold',
                }}
              >
                El país seleccionado no está soportado
              </Typography>
            )}
            {errors?.pickup?.address && (
              <Typography variant="body2" color={theme.palette.error.main}>
                {errors.pickup.address.message}
              </Typography>
            )}
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 12, xl: 6 }}>
            <InputField
              label="Ciudad *"
              name="pickup.city"
              errors={touchedFields?.pickup?.city ? errors : {}}
              register={register}
              sxInput={{ mt: 0 }}
              sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
              icon={<FmdGoodIcon />}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 12, xl: 6 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0rem',
                margin: '1rem 0.5rem 0.5rem 0',
              }}
            >
              <Icon color="disabled" sx={{ marginRight: '0.5rem' }}>
                <PublicOutlinedIcon />
              </Icon>
              <Typography
                variant="body1"
                sx={{
                  fontWeight: 'bold',
                  color: theme.palette.secondary.main,
                  fontSize: '1rem',
                }}
              >
                País
              </Typography>
            </Box>
            <TextField
              fullWidth
              value={userCountry}
              slotProps={{
                input: {
                  readOnly: true,
                },
              }}
              disabled
              placeholder="País del usuario"
              sx={{
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: theme.palette.text.primary,
                  opacity: 0.7,
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 12, xl: 6 }}>
            <InputField
              label={requiresZipcode ? 'Código postal *' : 'Código postal'}
              name="pickup.postalCode"
              errors={touchedFields?.pickup?.postalCode ? errors : {}}
              register={register}
              sxInput={{ mt: 0 }}
              sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
              icon={<FmdGoodIcon />}
            />
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={3}
        sx={{
          padding: theme.spacing(3),
          mt: 2,
        }}
      >
        <Typography variant="subtitle2" mb={2}>
          Información adicional
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6, lg: 12, xl: 6 }}>
            <InputField
              label="Referencia"
              name="pickup.reference"
              errors={touchedFields?.pickup?.reference ? errors : {}}
              register={register}
              sxInput={{ mt: 0 }}
              sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
              icon={<FmdGoodOutlinedIcon />}
              placeholder="Ej: Casa blanca, portón negro"
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6, lg: 12, xl: 6 }}>
            <InputField
              label="Notas adicionales"
              name="pickup.notes"
              errors={touchedFields?.pickup?.notes ? errors : {}}
              register={register}
              sxInput={{ mt: 0 }}
              sxLabel={{ color: theme.palette.secondary.main, fontSize: '1rem' }}
              icon={<NotesOutlinedIcon />}
              placeholder="Instrucciones especiales"
            />
          </Grid>
        </Grid>
      </Paper>

      <Grid
        container
        size={{ xxs: 12 }}
        mt={4}
        sx={{
          display: 'flex',
          flexDirection: { xxs: 'column-reverse', sm: 'row', lg: 'column-reverse', xl: 'row' },
          justifyContent: { xxs: 'center', sm: 'space-between', lg: 'center', xl: 'space-between' },
          width: '100%',
          alignItems: 'center',
          gap: '1rem',
        }}
      >
        <Button variant="outlined" onClick={() => setCurrentStep(step - 1)}>
          Atrás
        </Button>
        <Button
          disabled={isDisabled}
          onClick={() => (editStep ? setCurrentStep(5) : setCurrentStep(step + 1))}
        >
          Continuar
        </Button>
      </Grid>
    </Box>
  );
};

export default PickupDetails;
